import React, {
  useContext,
  useState,
  useEffect,
  Fragment,
  useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'

import { StepHandlerContext } from '../../contexts/stepHandler.context'
import { NextButtonAvailabilityContext } from '../../contexts/nextButtonAvailability.context'
import { windowAmplitudeTrack } from '../../../shared/helpers/utils'

import {
  setSubscriptionPatterns,
  setSubscriptionPatternSubSteps,
  setSubscriptionSizeSubSteps,
  setSubscriptionStep,
  setSubscriptionVariants
} from '../../store/subscription/subscription.action'
import {
  selectSubscriptionPatternSubSteps,
  selectSubscriptionStep,
  selectSubscriptionBundle,
  selectSubscriptionVariants
} from '../../store/subscription/subscription.selector'
import {
  MIX_BUNDLE,
  SIZE_STEP,
  UPSELL_STEP,
  DIAPERS_PATTERNS,
  PANTS_PATTERNS,
  PRODUCT_TYPES_DATA,
  SIZES_DATA,
  PATTERNS_DATA,
  ORIGINE_PATH
} from '../../utils/bab/bab.utils'

import Title from '../../components/title/title.component'
import Text from '../../components/text/text.component'
import TypeGrid from '../../components/type-grid/type-grid.component'
import PatternGrid from '../../components/pattern-grid/pattern-grid.component'
import PatternFocus from '../../components/pattern-focus/pattern-focus.component'

import './pattern.styles.scss'

const Pattern = () => {
  const { t } = useTranslation('updatedBAB')
  const dispatch = useDispatch()
  const history = useHistory()

  const [localPatterns, setLocalPatterns] = useState([])
  const localPatternsRef = useRef(localPatterns)

  const { setPreviousStep, setNextStep } = useContext(StepHandlerContext)
  const { nextButtonAvailability, setNextButtonAvailability } = useContext(
    NextButtonAvailabilityContext
  )

  const subscriptionBundle = useSelector(selectSubscriptionBundle)
  const subscriptionVariants = useSelector(selectSubscriptionVariants)
  const subscriptionStep = useSelector(selectSubscriptionStep)
  const subSteps = useSelector(selectSubscriptionPatternSubSteps)

  const isMixBundle = subscriptionBundle === MIX_BUNDLE.id

  const totalSubSteps = subSteps.total
  const completedSubSteps = subSteps.completed

  if (Object.keys(subscriptionVariants).length === 0) return null

  const sizeData = SIZES_DATA.find((size) => {
    const sizeId = Object.entries(subscriptionVariants)[completedSubSteps][0]

    return size.id === sizeId
  })

  const productTypeData = PRODUCT_TYPES_DATA.find((productType) => {
    return productType.sizes.includes(sizeData.id)
  })

  const isPantsProductType = productTypeData.id === 'pants'

  const productMaxQuantity = Object.entries(subscriptionVariants).find(
    ([sizeId], index) => {
      return sizeId === sizeData.id
    }
  )[1].length

  const typeGridData = []

  Object.entries(subscriptionVariants).map(([sizeId], index) => {
    const productType = PRODUCT_TYPES_DATA.find((productType) =>
      productType.sizes.includes(sizeId)
    )

    const size = SIZES_DATA.find((size) => size.id === sizeId)

    typeGridData.push({
      id: index,
      productTypeId: productType.id,
      sizeId: sizeId,
      translationKeys: {
        sizeShorthand: size.translationKeys.shortHand,
        productName: productType.translationKeys.singularName
      }
    })
  })

  const patternsData = isPantsProductType ? PANTS_PATTERNS : DIAPERS_PATTERNS

  // Cycle de vie composant
  useEffect(() => {
    const previousAvailability = nextButtonAvailability
    setPreviousStep(() => handlePreviousStep)
    setNextStep(() => handleNextStep)
    setNextButtonAvailability(false)

    const patternArray = Array.from({ length: productMaxQuantity }).fill('')
    setLocalPatterns(patternArray)

    return () => {
      setLocalPatterns({})
      setPreviousStep(() => {})
      setNextStep(() => {})
      setNextButtonAvailability(previousAvailability)
    }
  }, [subscriptionStep, subSteps])

  // Disponibilité du bouton suivant
  useEffect(() => {
    const patternsTotalQuantity = localPatterns.filter(
      (pattern) => pattern !== ''
    ).length

    localPatternsRef.current = localPatterns

    if (patternsTotalQuantity === productMaxQuantity) {
      completedSubSteps !== totalSubSteps
        ? handleNextStep()
        : setNextButtonAvailability(true)
    } else {
      setNextButtonAvailability(false)
    }
  }, [localPatterns])

  const handlePreviousStep = () => {
    if (completedSubSteps === 0) {
      dispatch(setSubscriptionVariants({}))
      if (subscriptionBundle === 'mix') {
        dispatch(setSubscriptionSizeSubSteps(1, 0))
      }
      dispatch(setSubscriptionStep(1))
      history.replace(SIZE_STEP.path)
    } else {
      dispatch(
        setSubscriptionPatternSubSteps(totalSubSteps, completedSubSteps - 1)
      )
    }
  }

  const handleNextStep = () => {
    windowAmplitudeTrack('BabDiaperPatternDone')
    let updatedPatternSelection = localPatternsRef.current
    dispatch(setSubscriptionPatterns(sizeData.id, updatedPatternSelection))

    if (completedSubSteps < totalSubSteps) {
      dispatch(
        setSubscriptionPatternSubSteps(totalSubSteps, completedSubSteps + 1)
      )
    } else {
      dispatch(setSubscriptionStep(3))
      history.replace(UPSELL_STEP.path)
    }
  }

  const handlePatternChange = (event) => {
    const targetElement = event.target
    const container = targetElement.closest('.pattern-selector-container')
    const minusButton = targetElement.closest('.pattern-choice-minus')
    const plusButton = targetElement.closest('.pattern-choice-plus')
    const focusButton = targetElement.closest('.pattern-glass')

    if ((!minusButton && !plusButton) || focusButton) return false

    const patternId = container.dataset.id

    const patternQuantity = localPatterns.reduce(
      (acc, id) => acc + (id === patternId ? 1 : 0),
      0
    )

    if (minusButton && patternQuantity > 0) {
      setLocalPatterns((prevState) => {
        const newPatternArray = [...prevState]

        let index = newPatternArray.lastIndexOf(patternId)
        if (index !== -1) {
          newPatternArray[index] = ''
        }

        return newPatternArray
      })
    } else if (plusButton && patternQuantity < productMaxQuantity) {
      setLocalPatterns((prevState) => {
        const newPatternArray = [...prevState]

        for (let i = 0; i < newPatternArray.length; i++) {
          if (newPatternArray[i] === '') {
            newPatternArray[i] = patternId
            break
          }
        }

        return newPatternArray
      })
    }
  }

  const handleFocusClick = (event) => {
    const targetElement = event.target
    const container = targetElement.closest('.pattern-selector-container')
    const focusButton = targetElement.closest('.pattern-glass')

    if (!focusButton) return false

    const patternId = container.getAttribute('data-id')

    const patternData = PATTERNS_DATA.find(
      (pattern) => pattern.id === patternId
    )

    const focusImageElement = document.querySelector('.focus-image')
    const focusNameElement = document.querySelector('.focus-name')

    focusImageElement.setAttribute(
      'src',
      productTypeData.id === 'pants'
        ? `${patternData.imageSources.pants}?height=480`
        : `${patternData.imageSources.diapers}?height=480`
    )

    focusNameElement.textContent = t(patternData.translationKeys.name)

    setTimeout(() => {
      document.querySelector('.focus-wrapper').setAttribute('data-open', '')
    }, 400)
  }

  const handleOriginClick = () => {
    window.open(ORIGINE_PATH, '_self')
  }

  return (
    <Fragment>
      <Helmet>
        <title>{t('pattern.metaData.title')}</title>
        <meta name='description' content={t('pattern.metaData.description')} />
      </Helmet>

      {isMixBundle && completedSubSteps === 0 ? (
        <Title
          content={t('pattern.title', {
            returnObjects: true,
            maxQuantity: productMaxQuantity,
            productName: t(productTypeData.translationKeys.lowerCaseName),
            sizeName: t(sizeData.translationKeys.lowerCaseName)
          })}
          quantity={isMixBundle ? 1 : totalSubSteps + 1}
        />
      ) : (
        <h1 className='title tile-style-1'>
          {t('pattern.title.firstLine')}
          <span>
            {t('pattern.title.mixDiapersSecondLine', {
              productName: t(productTypeData.translationKeys.lowerCaseName)
            })}
          </span>
        </h1>
      )}

      {typeGridData.length > 1 && (
        <TypeGrid
          typeGridData={typeGridData}
          step={subscriptionStep}
          completedSubSteps={completedSubSteps}
          margins={[16, 0, 16, 0]}
        />
      )}

      <Text
        content={t('pattern.text')}
        classNames='pattern-text'
        margins={[16, 0, 16, 0]}
      />

      <PatternGrid
        patternsData={patternsData}
        localPatterns={localPatterns}
        productTypeData={productTypeData}
        sizeData={sizeData}
        maxQuantity={productMaxQuantity}
        completedSubSteps={completedSubSteps}
        clickHandler={handlePatternChange}
        origineClickHandler={handleOriginClick}
        focusClickHandler={handleFocusClick}
      />

      <PatternFocus patterns={patternsData} productType={productTypeData.id} />
    </Fragment>
  )
}

export default Pattern
